import { computed, ref, watch } from "vue";
import i18n from "@/i18n";
import { createGlobalState } from "@vueuse/core";
import useApplicationsState from "./applicationsState";
import { usePromiseWrapper, useReadonlyWithSetter, useResponsive } from "@/composables/common";
import { ApplicationsHttpService } from "@/api/http/ApplicationsHttpService";
import type {
  DocumentOptions,
  FileWithBlobTableItem,
  DocumentTableItem,
} from "@/types/DocumentTypes";
import { makeRandomString } from "@/util/common";
import { ApplicationPrivateChannel } from "@/api/ws/channels/ApplicationPrivateChannel";
import type {
  DeleteDocumentPayload,
  RequiredDocument,
  UpdateDocumentPayload,
} from "@/api/http/schemas/documentsSchemas";
import useToastState from "@/composables/state/toastState";
import type { TableRow, TableRowStyleVariant } from "@/types/CommonTypes";
import { FILE_TYPE_OTHER } from "@/constants/files";
import type { Comment } from "@/api/http/schemas/commentsSchema";
import { renderCommentAsPlainText } from "@/lib/comments";
import { useAnalyticsState } from "@/composables/state/analyticsState";
import { AnalyticsEvent } from "@/enums/analytics";
import CommentSavedForFileEvent from "@/api/ws/events/CommentSavedForFileEvent";

const { t } = i18n.global;

export const useDocumentsState = createGlobalState(() => {
  const toast = useToastState();
  const { activeApplication, selectedApplication } = useApplicationsState();
  const { trackEvent } = useAnalyticsState();
  const { isMobile } = useResponsive();

  const defaultErrorHandler = (action: "get" | "upload" | "update" | "delete") => {
    toast.value.addMessage({
      title: t(`documents.errors.${action}`),
      type: "error",
    });
  };

  const [applicationId, setApplicationId] = useReadonlyWithSetter<string | null>(null);

  // Files that are uploading, or failed uploading, are "queued" here.
  const [queuedDocuments, setQueuedDocuments] = useReadonlyWithSetter<FileWithBlobTableItem[]>([]);

  const [uploadedDocuments, setUploadedDocuments] = useReadonlyWithSetter<DocumentTableItem[]>([]);
  const [requiredDocuments, setRequiredDocuments] = useReadonlyWithSetter<RequiredDocument[]>([]);

  const applicationChannel = ref<ApplicationPrivateChannel | null>(null);

  const { executeFetch: getRequiredDocuments, loading: requiredDocumentsIsLoading } =
    usePromiseWrapper(
      async (dealIdOverride?: string, params?: Record<string, unknown>) => {
        const dealId = dealIdOverride ?? applicationId.value;
        if (!dealId) {
          return;
        }

        const requiredDocuments = await new ApplicationsHttpService().getRequiredDocuments(
          dealId,
          params
        );

        requiredDocuments.sort((a, b) => {
          const isCompleteA = a.uploaded_count >= a.required_count;
          const isCompleteB = b.uploaded_count >= b.required_count;

          if (isCompleteA !== isCompleteB) {
            return isCompleteA ? 1 : -1;
          }

          return (a.weight ?? 0) - (b.weight ?? 0);
        });

        setRequiredDocuments(requiredDocuments);
        return requiredDocuments;
      },
      { onError: () => defaultErrorHandler("get") }
    );

  const { executeFetch: getUploadedDocuments } = usePromiseWrapper(
    async (dealIdOverride?: string) => {
      const dealId = dealIdOverride ?? applicationId.value;
      if (!dealId) {
        return;
      }

      const uploadedDocuments = await new ApplicationsHttpService().getUploadedDocuments(dealId, {
        include_content: true,
      });
      setUploadedDocuments(uploadedDocuments);
    },
    { onError: () => defaultErrorHandler("get") }
  );

  const { executeFetch: uploadDocuments, loading: uploadDocumentsIsLoading } = usePromiseWrapper(
    async ({
      files,
      documentOptions,
      uploadSource,
    }: {
      files: FileList;
      documentOptions?: DocumentOptions;
      uploadSource?: "drag" | "click";
    }) => {
      if (!applicationId.value) {
        return;
      }

      // Because multiple files can be uploaded as part of the same request, we
      // need a way to know which files were part of which request in the queue.
      const groupId = makeRandomString();

      const serializedFiles: FileWithBlobTableItem[] = Object.values(files).map((file) => ({
        id: makeRandomString(),
        original_name: file.name,
        file_size: Math.round(file.size / 1024), // in kbytes
        loading: true,
        failed: false,
        file,
        groupId,
        ...documentOptions,
      }));

      setQueuedDocuments([...queuedDocuments.value, ...serializedFiles]);

      // Can't use promise wrapper catch because we need to capture groupId.
      // Also, we need more custom error handling logic than provided by the
      // default error handler.
      try {
        const uploadedDocumentsResult = await new ApplicationsHttpService().addDocuments(
          applicationId.value,
          serializedFiles
        );
        await getRequiredDocuments();

        setQueuedDocuments(
          queuedDocuments.value.filter((document) => document.groupId !== groupId)
        );
        setUploadedDocuments([...uploadedDocumentsResult, ...uploadedDocuments.value]);

        trackEvent(AnalyticsEvent.DocumentUploaded, {
          application_id: applicationId.value,
          upload_source: uploadSource ?? "click",
          is_mobile: isMobile.value,
          file_count: uploadedDocumentsResult.length,
          documents: uploadedDocumentsResult.map(({ file_type, file_size, original_name }) => ({
            file_type,
            file_size,
            original_name,
          })),
        });

        toast.value.addMessage({
          title: t("documents.documentSuccessfullyUploaded"),
          type: "success",
        });
      } catch {
        trackEvent(AnalyticsEvent.DocumentUploadFailed, {
          application_id: applicationId.value,
          error_type: "upload_error",
          file_count: serializedFiles.length,
          documents: serializedFiles.map(({ file_type, file_size, original_name }) => ({
            file_type,
            file_size,
            original_name,
          })),
        });

        defaultErrorHandler("upload");
        setQueuedDocuments(
          queuedDocuments.value.map((queuedDocument) => ({
            ...queuedDocument,
            loading: queuedDocument.groupId === groupId ? false : queuedDocument.loading,
            failed: queuedDocument.groupId === groupId ? true : queuedDocument.failed,
          }))
        );
      }
    },
    {
      // This is still here in the case of a missing application id.
      onError: () => defaultErrorHandler("upload"),
    }
  );

  const { executeFetch: retryFailedUpload } = usePromiseWrapper(async (fileId: number | string) => {
    if (!applicationId.value) {
      return;
    }

    const fileToRetry = queuedDocuments.value.find((file) => file.id === fileId);

    if (!fileToRetry || !fileToRetry.failed) {
      return;
    }

    try {
      setDocumentAsLoading(fileId);
      const uploadedDocumentsResult = await new ApplicationsHttpService().addDocuments(
        applicationId.value,
        [fileToRetry]
      );
      setQueuedDocuments(queuedDocuments.value.filter((document) => document.id !== fileId));
      setUploadedDocuments([...uploadedDocumentsResult, ...uploadedDocuments.value]);
    } catch {
      fileToRetry.loading = false;
      fileToRetry.failed = true;
      defaultErrorHandler("upload");
    }
  });

  const { executeFetch: updateDocument } = usePromiseWrapper(
    async (documentId: number, updateDocumentPayload: UpdateDocumentPayload) => {
      if (!applicationId.value) {
        return;
      }

      setDocumentAsLoading(documentId);

      try {
        const result = await new ApplicationsHttpService().updateDocument(
          applicationId.value,
          documentId,
          updateDocumentPayload
        );
        await getRequiredDocuments();

        setUploadedDocuments(
          uploadedDocuments.value.map((document) =>
            document.id === documentId ? result : document
          )
        );
      } catch {
        cancelDocumentLoading(documentId);
        defaultErrorHandler("update");
      }
    }
  );

  const { executeFetch: deleteDocument } = usePromiseWrapper(
    async (fileId, deleteDocumentsPayload: DeleteDocumentPayload) => {
      if (!applicationId.value) {
        return;
      }

      // Capture document info before deletion for analytics
      const deletedDoc = uploadedDocuments.value.find(({ id }) => id === fileId);

      setDocumentAsLoading(fileId);

      try {
        await new ApplicationsHttpService().deleteDocument(
          applicationId.value,
          fileId,
          deleteDocumentsPayload
        );
        await getRequiredDocuments();

        setUploadedDocuments(uploadedDocuments.value.filter((document) => document.id !== fileId));

        trackEvent(AnalyticsEvent.DocumentDeleted, {
          application_id: applicationId.value,
          file_type: deletedDoc?.file_type ?? "unknown",
          file_size: deletedDoc?.file_size ?? 0,
          original_name: deletedDoc?.original_name ?? "unknown",
        });
      } catch {
        cancelDocumentLoading(fileId);
        defaultErrorHandler("delete");
      }
    }
  );

  const tableRows = computed<TableRow<DocumentTableItem>[]>(() => {
    return [...queuedDocuments.value, ...uploadedDocuments.value].map((tableItem) => {
      const tableRow = {
        data: tableItem as DocumentTableItem,
        options: {
          variant: "default" as TableRowStyleVariant,
          validationErrors: [] as string[],
          inactive: false as boolean,
        },
      } satisfies TableRow<DocumentTableItem>;

      const hasComments = (tableRow.data.comments as Comment[])?.some(
        (comment) => !comment.is_resolved
      );
      const failed = tableRow.data.failed || hasComments;

      if (hasComments) {
        tableRow.options.validationErrors = (tableRow.data.comments as Comment[])
          .filter((comment) => !comment.is_resolved)
          .map(renderCommentAsPlainText);
      }

      if (failed) {
        tableRow.options.variant = "error";
      } else if (tableRow.data.file_type === FILE_TYPE_OTHER) {
        tableRow.options.variant = "warning";
      }

      return tableRow;
    });
  });

  const isMissingRequiredDocuments = computed(() =>
    requiredDocuments.value.some(
      (requiredDocumentCollection) =>
        requiredDocumentCollection.required_count > requiredDocumentCollection.uploaded_count
    )
  );

  const documentsWithIssues = computed(() => {
    return uploadedDocuments.value.filter((document) =>
      document.comments?.some((comment) => !comment.is_resolved)
    );
  });

  const hasDocumentsWithIssues = computed(() => {
    return documentsWithIssues.value.length;
  });

  const setDocumentAsLoading = (fileId: string | number) => {
    const collection = typeof fileId === "number" ? uploadedDocuments : queuedDocuments;
    const fileIndex = collection.value.findIndex((document) => document.id === fileId);

    if (fileIndex === -1) {
      return;
    }

    collection.value[fileIndex].loading = true;
    collection.value[fileIndex].failed = false;
  };

  const cancelDocumentLoading = (fileId: string | number) => {
    const collection = typeof fileId === "number" ? uploadedDocuments : queuedDocuments;
    const fileIndex = collection.value.findIndex((document) => document.id === fileId);

    if (fileIndex === -1) {
      return;
    }

    collection.value[fileIndex].loading = false;
  };

  const documentIsUploaded = (
    model: DocumentTableItem | FileWithBlobTableItem
  ): model is DocumentTableItem => {
    return typeof model.id === "number";
  };

  watch(
    () => [activeApplication.value?.id, selectedApplication.value?.id],
    ([newActiveApplicationId, newSelectedApplicationId]) => {
      setApplicationId(newActiveApplicationId ?? newSelectedApplicationId ?? null);
    },
    { immediate: true }
  );

  watch(
    () => applicationId.value,
    async () => {
      if (!applicationId.value) {
        return;
      }

      await getUploadedDocuments();
      await getRequiredDocuments();
    },
    { immediate: true }
  );

  watch(
    applicationId,
    async (newId, oldId) => {
      if (!newId || !!oldId) {
        applicationChannel.value?.leave();
      }

      if (!newId || newId === oldId) {
        return;
      }

      applicationChannel.value = new ApplicationPrivateChannel(newId);
      applicationChannel.value?.subscribeTo(
        new CommentSavedForFileEvent((updatedComment) => {
          const documentToUpdate = uploadedDocuments.value.find(
            (doc) => doc.id === Number(updatedComment.commentable_id)
          );

          if (!documentToUpdate) {
            return;
          }

          documentToUpdate.comments ??= [];

          const commentIndex = documentToUpdate.comments.findIndex(
            ({ id }) => id === updatedComment.id
          );

          const commentIsVisibleToBorrower =
            updatedComment.is_shared_with_borrower &&
            !updatedComment.is_resolved &&
            Boolean(updatedComment.content?.content);
          const commentAlreadyExists = commentIndex !== -1;

          if (!commentAlreadyExists && commentIsVisibleToBorrower) {
            documentToUpdate.comments.push(updatedComment);
          } else if (commentAlreadyExists && !commentIsVisibleToBorrower) {
            documentToUpdate.comments.splice(commentIndex, 1);
          } else if (commentAlreadyExists && commentIsVisibleToBorrower) {
            documentToUpdate.comments[commentIndex] = updatedComment;
          }
        })
      );
    },
    { immediate: true }
  );

  return {
    documentsWithIssues,
    hasDocumentsWithIssues,
    isMissingRequiredDocuments,
    queuedDocuments,
    requiredDocuments,
    requiredDocumentsIsLoading,
    tableRows,
    uploadDocumentsIsLoading,
    uploadedDocuments,
    deleteDocument,
    documentIsUploaded,
    getRequiredDocuments,
    getUploadedDocuments,
    retryFailedUpload,
    setApplicationId,
    setQueuedDocuments,
    setRequiredDocuments,
    setUploadedDocuments,
    uploadDocuments,
    updateDocument,
  };
});
